/*
 * Copyright (c) 2020. American Express Company
 */

import { LocalDate } from '@js-joda/core';
import _ from 'lodash';

import { Board, SortOrder } from 'src/bw/board/Board';
import { AccountModel } from 'src/types/account.model';
import { CardModel } from 'src/types/card.model';
import { CashFlowModel } from 'src/types/cash-flow.model';
import { CategoryModel } from 'src/types/category.model';
import { IncomeSourceRuleModel } from 'src/types/income-source-rule.model';
import { IncomeSourceModel } from 'src/types/income-source.model';
import { IncomeAggregate } from 'src/types/income.model';
import { TransactionModel } from 'src/types/transaction.model';
import { FullUserModel } from 'src/types/user.model';
import CashFlowService from '../../services/cashflow.service';
import CategoryService from '../../services/category.service';
import FormatService from '../../services/format.service';
import IncomeSourceRuleService from '../../services/income-source-rule.service';
import IncomeSourceService from '../../services/income-source.service';
import MixpanelService from '../../services/mixpanel.service';
import NavigationService from '../../services/navigation.service';
import TransactionService from '../../services/transaction.service';
import PersonService from '../../services/person.service';
import { RuleMatcher } from 'src/types/ruleMatcher.enum';

export default class IncomeFactory {
    private readonly component: Board;

    constructor(component: any) {
        this.component = component;
    }

    public async handleAddIncome(): Promise<void> {
        NavigationService.goTo(this.component.props.history, '/income/new', 'New Income drawer').catch(console.error);
    }

    public async handleCancelIncome(): Promise<void> {
        this.component.setState({
            currentIncomeSource: null
        });
        this.component.drawerFactory.handleTopDrawer();
    }

    // TODO this may not be needed anymore
    public handleChangeCard(): void {
        // this.component.drawerFactory.updateDrawer({
        //     ...this.component.state.drawerProps,
        // });
    }

    public async handleDeleteIncome(incomeSource: IncomeSourceModel): Promise<void> {
        await IncomeSourceService.deleteIncomeSource(incomeSource);
        this.component.setState({
            currentIncomeSource: null,
            incomeSources: _.sortBy(await IncomeSourceService.getIncomeSources(), (i) => i.name),
            incomeChanged: true
        });
    }

    public async handleDisplayIncome(aggregate: IncomeAggregate): Promise<void> {
        for (const transaction of aggregate.transactions) {
            transaction.ignore = !aggregate.ignore;
            await TransactionService.updateTransaction(transaction);
        }

        this.component.setState({
            currentTransactions: this.component.state.currentTransactions,
            transactionsChanged: true,
            incomeChanged: true
        });
    }

    public async handleEditIncome(incomeSource: IncomeSourceModel): Promise<void> {
        NavigationService.goTo(
            this.component.props.history,
            '/income/' + incomeSource.incomeUid,
            'Income drawer'
        ).catch(console.error);
    }

    public handleIncomeChange(event: any, name: string, type: string = 'none'): void {
        // The new DLS components return a value directly, not an event
        const value: number | string = _.get(event, 'target.value', event);
        const incomeSource: IncomeSourceModel = { ...this.component.state.drawerProps.currentIncomeSource };
        if (type === 'currency') {
            (incomeSource as any)[name] = Number(String(value).replace(/[$,]/g, ''));
        } else {
            (incomeSource as any)[name] = value;
        }

        this.component.drawerFactory.updateDrawer({
            ...this.component.state.drawerProps,
            currentIncomeSource: incomeSource
        });
    }

    public handleIncomeDateChange(date: Date, name: string): void {
        const incomeSource: IncomeSourceModel = { ...this.component.state.drawerProps.currentIncomeSource };
        (incomeSource as any)[name] = date;
        this.component.setState({
            currentIncomeSource: incomeSource,
            incomeChanged: true
        });
        this.component.drawerFactory.updateDrawer({
            ...this.component.state.drawerProps,
            currentIncomeSource: incomeSource
        });
    }

    public async handleOpenDefaultIncomeSummary(): Promise<void> {
        const offset: number =
            CashFlowService.getCashFlowIndexWithDate(this.component.props.user.cashFlow, LocalDate.now()) - 1;
        const cashFlow: CashFlowModel[] = this.component.props.user.cashFlow;
        await this.handleOpenIncomeSummary(cashFlow[offset].startDate, cashFlow[offset].endDate);
    }

    public async handleOpenIncome(
        incomeSource: IncomeSourceModel,
        startDate: LocalDate,
        endDate: LocalDate
    ): Promise<void> {
        NavigationService.goTo(
            this.component.props.history,
            `/drawer/transactions?start=${FormatService.formatSqlDate(startDate)}&end=${FormatService.formatSqlDate(
                endDate
            )}&incomesource=${incomeSource.incomeUid}`,
            'Transactions drawer'
        ).catch(console.error);
    }

    public async openIncomeRoute(incomeSourceUid: string, startDate: LocalDate, endDate: LocalDate): Promise<void> {
        let transactions: TransactionModel[] = await this.component.getTransactions(startDate, endDate);
        transactions = _.filter(transactions, { incomeUid: incomeSourceUid });

        let accountsById: Map<number, AccountModel> = this.component.props.userAccounts;

        if (!accountsById) {
            const accounts = this.component.props.user.accounts;
            accountsById = new Map<number, AccountModel>();
            for (const account of accounts) {
                accountsById.set(account.id, account);
            }
        }

        const sort: SortOrder = { sort: 'date', order: -1 };
        transactions = this.component.transactionFactory.sortTransactions(transactions, 'date', -1);
        const accounts = this.component.props.user.accounts;
        const incomeSources = await IncomeSourceService.getIncomeSources();
        const incomeSource: IncomeSourceModel = _.find(incomeSources, { incomeUid: incomeSourceUid });

        this.component.setState({
            drawerState: 'income-details',
            drawerProps: {
                accounts,
                startDate,
                endDate,
                incomeSource,
                incomeSources: _.sortBy(incomeSources, (i) => i.name),
                monthsPerPeriod: this.component.state.monthsPerPeriod,
                openedPayer: null,
                transactions,
                transactionSort: sort,
                type: 'income',
                user: this.component.props.user,
                userAccounts: accountsById,
                handleAdd: this.handleAddIncome.bind(this)
            }
        });
    }

    public async handleOpenIncomeSummary(startDate: LocalDate, endDate: LocalDate): Promise<void> {
        NavigationService.goTo(
            this.component.props.history,
            `/drawer/incomes/${FormatService.formatSqlDate(startDate)}/${FormatService.formatSqlDate(endDate)}`,
            'Incomes drawer'
        ).catch(console.error);
    }

    public async openIncomeSummaryRoute(startDate: LocalDate, endDate: LocalDate): Promise<void> {
        let accountsById: Map<number, AccountModel> = this.component.props.userAccounts;

        if (!accountsById) {
            const accounts = this.component.props.user.accounts;
            accountsById = new Map<number, AccountModel>();
            for (const account of accounts) {
                accountsById.set(account.id, account);
            }
        }

        let transactions: TransactionModel[] = await this.component.getTransactions(startDate, endDate);
        let incomeSources: IncomeSourceModel[] = await IncomeSourceService.getIncomeSources();
        incomeSources = _.filter(
            incomeSources,
            (income) => (!income.startDate || income.startDate.isBefore(endDate)) && income.startedByCardId !== null
        );
        transactions = _.filter(
            transactions,
            (transaction: TransactionModel) => transaction.type !== 'TRANSACTION_EXPENSE'
        );

        transactions = this.component.transactionFactory.sortTransactions(transactions, 'date', -1);
        this.component.setState({
            userAccounts: accountsById,
            currentTransactions: transactions,
            drawerState: 'incomes',
            drawerProps: {
                cashFlow: this.component.props.user.cashFlow,
                startDate,
                endDate,
                monthsPerPeriod: this.component.state.monthsPerPeriod,
                transactions,
                incomeSources: _.sortBy(incomeSources, (i) => i.name),
                transactionSummary: this.component.state.transactionSummary,
                openedPayer: null,
                persons: PersonService.getPersonsFromUser(this.component.props.user),
                userAccounts: accountsById
            }
        });
        window.dispatchEvent(new Event('resize'));
    }

    public async handleAddIncomeRule(
        transaction: TransactionModel,
        incomeUid: string,
        matcher: RuleMatcher,
        transactionSubstring: string,
        remove: boolean = false
    ): Promise<void> {
        const drawerProps: any = this.component.state.drawerProps;

        // make a rule
        const newRule = new IncomeSourceRuleModel();
        newRule.matcher = matcher;
        newRule.transactionNamePattern = transactionSubstring;
        newRule.incomeUid = incomeUid;

        await IncomeSourceRuleService.createRule(newRule);

        let transactions: TransactionModel[] = drawerProps.transactions;
        const matched: TransactionModel[] = [];

        for (const transaction of drawerProps.transactions) {
            if (IncomeSourceRuleService.matchesRule(transaction, newRule)) {
                matched.push(transaction);
            }
        }

        const incomeSources = await IncomeSourceService.getIncomeSources();
        const index = _.findIndex(incomeSources, { incomeUid });

        if (index !== -1) {
            const incomeSourceUid = incomeSources[index].incomeUid;

            for (const transaction of matched) {
                transaction.incomeUid = incomeSourceUid;
                await TransactionService.updateTransaction(transaction);

                if (remove) {
                    transactions = _.pull(transactions, transaction);
                } else {
                    const index = _.findIndex(transactions, { id: transaction.id });
                    if (index !== -1) {
                        transactions[index] = transaction;
                    }
                }
            }
        }

        if (transactions.length > 0) {
            this.component.drawerFactory.updateDrawer({
                ...this.component.state.drawerProps,
                transactions,
                incomeSources,
                transactionsChanged: true
            });
        } else {
            // TODO could skip the duplicate server round trips with setDrawer and etc here
            await this.handleOpenIncomeSummary(drawerProps.startDate, drawerProps.endDate);
        }
    }

    public async handleSaveIncome(user: FullUserModel, incomeSource: IncomeSourceModel): Promise<void> {
        if (!incomeSource.name) {
            return await this.handleCancelIncome();
        }

        if (incomeSource.incomeUid) {
            await IncomeSourceService.updateIncomeSource(incomeSource);
        } else {
            // ignore return
            await IncomeSourceService.createIncomeSource(incomeSource);
        }

        this.component.setState({
            currentIncomeSource: null,
            incomeSources: await IncomeSourceService.getIncomeSources()
        });

        await this.component.handleReloadUser();
    }

    public async handleOpenRecentIncomeSummary(user: FullUserModel): Promise<void> {
        const offset: number = CashFlowService.getCashFlowIndexWithDate(user.rawCashFlow, LocalDate.now());
        const startDate: LocalDate = user.rawCashFlow[offset - 12].startDate;
        const endDate: LocalDate = user.rawCashFlow[offset - 1].endDate;

        await this.handleOpenIncomeSummary(startDate, endDate);
    }

    public async handleOpenReviewIncomeTransactions(mentorInitiated: boolean): Promise<void> {
        NavigationService.goTo(
            this.component.props.history,
            `/review/income/${mentorInitiated}`,
            'Income drawer'
        ).catch(console.error);
    }

    public async openReviewIncomeTransactionsRoute(mentorInitiated: boolean): Promise<any> {
        let transactions: TransactionModel[] = await TransactionService.getTransactionsForReview();
        transactions = _.filter(transactions, (transaction: TransactionModel) => {
            return transaction.type === 'TRANSACTION_INCOME';
        });

        let back: () => any;

        if (mentorInitiated) {
            back = async () => {
                await this.component.handleUpdateUserAttribute('reviewedIncome', true);
                await this.component.handleReloadUser();
                this.component.drawerFactory.handleTopDrawer();
                await this.component.mentorFactory.processAction('continue-step-1');
            };
        }

        const incomeSources = await IncomeSourceService.getIncomeSources();
        const categories: CategoryModel[] = await CategoryService.getCategories();
        const accounts = this.component.props.user.accounts;
        const sort: SortOrder = { sort: 'date', order: 1 };

        this.component.setState({
            drawerState: 'review-transactions',
            drawerProps: {
                type: 'income',
                incomeSources,
                transactions,
                transactionSort: sort,
                transactionsChanged: false,
                selectedTransaction: null,
                selectedTransactionGroup: null,
                accounts,
                handleBack: back,
                categories
            }
        });

        MixpanelService.track('Reviewed Incomes', {
            'Transactions To Review': this.component.state.user.incomeTransactionsToReviewCount,
            'Mentor Initiated': mentorInitiated
        });
    }

    /** Get a list of valid income source's that an end of job card could be assigned to
     * 1. Allow the currently selected income source
     * 2. Do not allow income source's that other Retire/Quit/Lose-job's are pointed to
     * 3. Do not allow income source's that end before this card
     */
    public validIncomeSourcesToEnd(card: CardModel, incomeSources: IncomeSourceModel[]): IncomeSourceModel[] {
        const END_JOB_GOALS: string[] = ['retire', 'quit job', 'lose job'];
        const incomesAlreadyInCards: string[] = _(this.component.props.user.cards)
            .filter((card) => END_JOB_GOALS.includes(card.description))
            .filter((aCard) => aCard.id !== card.id) // ignore this card, so you can switch back
            .flatMap((card) => card.modifyIncomes)
            .value();

        return incomeSources.filter(
            (incomeSource) =>
                // include if it's already ended by this card
                card.modifyIncomes.includes(incomeSource.incomeUid) ||
                !(
                    // exclude if it's already ended by another card (even future)
                    (
                        incomesAlreadyInCards.includes(incomeSource.incomeUid) ||
                        // exclude if it ended before this card
                        incomeSource?.endDate?.isBefore(card.startDate) ||
                        incomeSource.startDate > card.startDate
                    )
                )
        );
    }

    /** Get a list of valid income sources that this card could be assigned to
     * 1. Allow the currently selected income source, unless it is invalid
     * 2. Do not allow income sources that end before this card or start after
     */
    public validIncomeSources(card: CardModel, incomeSources: IncomeSourceModel[]): IncomeSourceModel[] {
        return incomeSources.filter(
            (incomeSource) =>
                !(
                    // exclude if it ended before this card or started after this card
                    (incomeSource.endDate?.isBefore(card.startDate) || incomeSource.startDate > card.startDate)
                )
        );
    }
}

export function canHaveIncomeSources(card: CardModel): boolean {
    return (
        card.description === 'quit job' ||
        card.description === 'lose job' ||
        card.description === 'retire' ||
        card.description === 'get promoted'
    );
}
